import { SortableContext } from "@dnd-kit/sortable";
import type { BlockRendererMapping } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type {
  BaseBlock,
  RootBlock,
} from "@circle-react-shared/BuilderUI/DataModel.types";
import { DroppableArea } from "@circle-react-shared/BuilderUI/DragAndDrop/DroppableArea";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import type { BlockCategoryName } from "@circle-react-shared/BuilderUI/constants/BlockCategories";
import { Preview } from "./Preview";
import { SortablePreview } from "./SortablePreview";

export interface PreviewBlockListProps {
  accepts: BlockCategoryName[];
  blocks: BaseBlock[];
  components: BlockRendererMapping;
  path: string;
  rejects: BlockCategoryName[];
  shouldShowSelectionOutlineFn: (block: RootBlock | BaseBlock) => boolean;
}

export const PreviewBlockList = ({
  blocks,
  components,
  path,
  accepts,
  rejects,
  shouldShowSelectionOutlineFn,
}: PreviewBlockListProps) => {
  const isDraggingEnabled = useBuilderStore(state => state.isDraggingEnabled);
  const isSortingEnabled = useBuilderStore(state => state.isSortingEnabled);
  const relativeChildrenPath = path
    .replace(/^blocks\./, "")
    .replace(/\.value+$/, "");

  if (isDraggingEnabled && isSortingEnabled) {
    return (
      <SortableContext items={blocks.map(block => block.id)}>
        {blocks.map((block, index) => (
          <SortablePreview
            key={block.id}
            block={block}
            components={components}
            path={`${path}.${index}`}
            shouldShowSelectionOutlineFn={shouldShowSelectionOutlineFn}
          />
        ))}
      </SortableContext>
    );
  }

  if (isDraggingEnabled) {
    return (
      <>
        {blocks.map((block, index) => (
          <Preview
            key={block.id}
            block={block}
            components={components}
            path={`${path}.${index}`}
            shouldShowSelectionOutlineFn={shouldShowSelectionOutlineFn}
          />
        ))}
        <DroppableArea
          id={relativeChildrenPath}
          accepts={accepts}
          rejects={rejects}
        />
      </>
    );
  }

  return (
    <>
      {blocks.map((block, index) => (
        <Preview
          key={block.id}
          block={block}
          components={components}
          path={`${path}.${index}`}
          shouldShowSelectionOutlineFn={shouldShowSelectionOutlineFn}
        />
      ))}
    </>
  );
};
